import React, { useState, useEffect } from "react";
import { MdSchool, MdAdd, MdFilterList, MdHowToReg } from "react-icons/md";
import InscriptionServiceInstance from "service/inscription.service";
import MembreServiceInstance from "service/membre.service";
import NiveauServiceInstance from "service/niveau.service";
import ParcoursServiceInstance from "service/parcours.service";
import MentionServiceInstance from "service/mention.service";
import PromotionServiceInstance from "service/promotion.service";
import HeaderSection from "components/headers-section";
import ActionBar from "components/action-bar";
import InscriptionTable from "./components/InscriptionTable";
import InscriptionFormModal from "./components/InscriptionFormModal";
import InscriptionWithMembreFormModal from "./components/InscriptionWithMembreFormModal";
import DeleteConfirmationModal from "components/delete-confirm-modal";
import InscriptionFilters from "./components/InscriptionFilters";
import InscriptionDetailModal from "./components/InscriptionDetailModal";
import AnneUniversitaireServiceInstance from "service/anneuniversitaire.service";

const InscriptionManager = () => {
  const [inscriptions, setInscriptions] = useState([]);
  const [membres, setMembres] = useState([]);
  const [anneesUniv, setAnneesUniv] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [parcours, setParcours] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [filteredInscriptions, setFilteredInscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isNewMembreModalOpen, setIsNewMembreModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentInscription, setCurrentInscription] = useState({
    id_membre: "",
    id_anneuniv: "",
    id_niveau: "",
    id_parcours: "",
    id_mention: "",
    date_inscrit: new Date().toISOString().split("T")[0],
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    anneeUniv: "",
    niveau: "",
    parcours: "",
    mention: "",
    dateDebut: "",
    dateFin: "",
  });

  useEffect(() => {
    fetchInscriptions();
    fetchMembres();
    fetchAnneesUniv();
    fetchNiveaux();
    fetchParcours();
    fetchMentions();
    fetchPromotions();
  }, []);

  useEffect(() => {
    filterInscriptions();
  }, [searchTerm, inscriptions, filters]);

  const fetchInscriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await InscriptionServiceInstance.getAll();
      setInscriptions(result || []);
      setFilteredInscriptions(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les inscriptions. Veuillez réessayer plus tard."
      );
      console.error("Error fetching inscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembres = async () => {
    try {
      const result = await MembreServiceInstance.getAll();
      setMembres(result || []);
    } catch (error) {
      console.error("Error fetching membres:", error);
    }
  };

  const fetchAnneesUniv = async () => {
    try {
      const result = await AnneUniversitaireServiceInstance.getAll();
      setAnneesUniv(result || []);
    } catch (error) {
      console.error("Error fetching années universitaires:", error);
    }
  };

  const fetchNiveaux = async () => {
    try {
      const result = await NiveauServiceInstance.getAll();
      setNiveaux(result || []);
    } catch (error) {
      console.error("Error fetching niveaux:", error);
    }
  };

  const fetchParcours = async () => {
    try {
      const result = await ParcoursServiceInstance.getAll();
      setParcours(result || []);
    } catch (error) {
      console.error("Error fetching parcours:", error);
    }
  };

  const fetchMentions = async () => {
    try {
      const result = await MentionServiceInstance.getAll();
      setMentions(result || []);
    } catch (error) {
      console.error("Error fetching mentions:", error);
    }
  };

  const fetchPromotions = async () => {
    try {
      const result = await PromotionServiceInstance.getAll();
      setPromotions(result || []);
    } catch (error) {
      console.error("Error fetching promotions:", error);
    }
  };

  const filterInscriptions = () => {
    let filtered = [...inscriptions];

    // Recherche textuelle
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (inscription) =>
          (inscription.nom_prenom_membre &&
            inscription.nom_prenom_membre.toLowerCase().includes(search)) ||
          (inscription.niveau &&
            inscription.niveau.toLowerCase().includes(search)) ||
          (inscription.name_parcours &&
            inscription.name_parcours.toLowerCase().includes(search)) ||
          (inscription.name_mention &&
            inscription.name_mention.toLowerCase().includes(search)) ||
          (inscription.dateuniv &&
            inscription.dateuniv.toLowerCase().includes(search))
      );
    }

    // Filtre par année universitaire
    if (filters.anneeUniv) {
      filtered = filtered.filter(
        (inscription) => inscription.dateuniv === filters.anneeUniv
      );
    }

    // Filtre par niveau
    if (filters.niveau) {
      filtered = filtered.filter(
        (inscription) => inscription.niveau === filters.niveau
      );
    }

    // Filtre par parcours
    if (filters.parcours) {
      filtered = filtered.filter(
        (inscription) => inscription.name_parcours === filters.parcours
      );
    }

    // Filtre par mention
    if (filters.mention) {
      filtered = filtered.filter(
        (inscription) => inscription.name_mention === filters.mention
      );
    }

    // Filtre par date de début
    if (filters.dateDebut) {
      filtered = filtered.filter(
        (inscription) =>
          new Date(inscription.date_inscrit) >= new Date(filters.dateDebut)
      );
    }

    // Filtre par date de fin
    if (filters.dateFin) {
      filtered = filtered.filter(
        (inscription) =>
          new Date(inscription.date_inscrit) <= new Date(filters.dateFin)
      );
    }

    setFilteredInscriptions(filtered);
  };

  const openCreateReinscriptionModal = () => {
    setCurrentInscription({
      id_membre: "",
      id_anneuniv: "",
      id_niveau: "",
      id_parcours: "",
      id_mention: "",
      date_inscrit: new Date().toISOString().split("T")[0],
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openCreateInscriptionModal = () => {
    setCurrentInscription({
      createMembreDto: {
        nom_prenom_membre: "",
        com_origin: "",
        adresse_membre: "",
        tel_membre: "",
        num_matricule: "",
        cin_membre: "",
        id_promotion: "",
      },
      id_anneuniv: "",
      id_niveau: "",
      id_parcours: "",
      id_mention: "",
      date_inscrit: new Date().toISOString().split("T")[0],
    });
    setIsEditing(false);
    setIsNewMembreModalOpen(true);
  };

  const openEditModal = (inscription) => {
    setCurrentInscription({
      id_inscrit: inscription.id_inscrit,
      id_membre: inscription.id_membre,
      id_anneuniv: inscription.id_anneuniv,
      id_niveau: inscription.id_niveau,
      id_parcours: inscription.id_parcours,
      id_mention: inscription.id_mention,
      date_inscrit: inscription.date_inscrit,
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (inscription) => {
    setCurrentInscription(inscription);
    setIsDeleteModalOpen(true);
  };

  const openDetailModal = (inscription) => {
    setCurrentInscription(inscription);
    setIsDetailModalOpen(true);
  };

  const handleSubmitReinscription = async (formData) => {
    setLoading(true);
    try {
      if (isEditing) {
        await InscriptionServiceInstance.update(
          currentInscription.id_inscrit,
          formData
        );
      } else {
        await InscriptionServiceInstance.create(formData);
      }

      fetchInscriptions();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: error.response?.data?.errors || {
          general: isEditing
            ? "Impossible de modifier l'inscription. Veuillez réessayer."
            : "Impossible de créer l'inscription. Veuillez réessayer.",
        },
      };
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitNewInscription = async (formData) => {
    setLoading(true);
    try {
      await InscriptionServiceInstance.createWithMembre(formData);

      fetchInscriptions();
      fetchMembres();
      setIsNewMembreModalOpen(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: error.response?.data?.errors || {
          general: "Impossible de créer l'inscription. Veuillez réessayer.",
        },
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await InscriptionServiceInstance.delete(currentInscription.id_inscrit);
      fetchInscriptions();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer l'inscription. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const resetFilters = () => {
    setFilters({
      anneeUniv: "",
      niveau: "",
      parcours: "",
      mention: "",
      dateDebut: "",
      dateFin: "",
    });
    setSearchTerm("");
  };

  // Calculer les statistiques
  const calculerStats = () => {
    const totalInscriptions = filteredInscriptions.length;

    // Compter les inscriptions par niveau
    const inscriptionsParNiveau = {};
    filteredInscriptions.forEach((inscription) => {
      if (inscription.niveau) {
        inscriptionsParNiveau[inscription.niveau] =
          (inscriptionsParNiveau[inscription.niveau] || 0) + 1;
      }
    });

    // Trouver les parcours les plus populaires
    const inscriptionsParParcours = {};
    filteredInscriptions.forEach((inscription) => {
      if (inscription.name_parcours) {
        inscriptionsParParcours[inscription.name_parcours] =
          (inscriptionsParParcours[inscription.name_parcours] || 0) + 1;
      }
    });

    // Trouver les années universitaires les plus représentées
    const inscriptionsParAnnee = {};
    filteredInscriptions.forEach((inscription) => {
      if (inscription.dateuniv) {
        inscriptionsParAnnee[inscription.dateuniv] =
          (inscriptionsParAnnee[inscription.dateuniv] || 0) + 1;
      }
    });

    return {
      totalInscriptions,
      inscriptionsParNiveau,
      inscriptionsParParcours,
      inscriptionsParAnnee,
    };
  };

  const stats = calculerStats();

  // Obtenir les années, niveaux, parcours et mentions uniques pour les filtres
  const getUniqueValues = (field) => {
    return [...new Set(inscriptions.map((item) => item[field]))].filter(
      Boolean
    );
  };

  const uniqueAnnees = getUniqueValues("dateuniv");
  const uniqueNiveaux = getUniqueValues("niveau");
  const uniqueParcours = getUniqueValues("name_parcours");
  const uniqueMentions = getUniqueValues("name_mention");

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-8 md:px-8">
      <HeaderSection
        title="Gestion des Inscriptions"
        description="Gérez les inscriptions et réinscriptions des étudiants"
        accentColor="#8B5CF6"
      />

      {/* Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-md border border-purple-200 bg-purple-50 p-4">
          <h3 className="text-sm font-medium text-purple-900">
            Total des inscriptions
          </h3>
          <p className="mt-2 text-xl font-bold text-purple-700">
            {stats.totalInscriptions}
          </p>
        </div>

        <div className="rounded-md border border-indigo-200 bg-indigo-50 p-4">
          <h3 className="text-sm font-medium text-indigo-900">Par niveau</h3>
          <div className="mt-2">
            {Object.entries(stats.inscriptionsParNiveau)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3)
              .map(([niveau, count], index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-indigo-700">{niveau}</span>
                  <span className="font-medium text-indigo-900">{count}</span>
                </div>
              ))}
          </div>
        </div>

        <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
          <h3 className="text-sm font-medium text-blue-900">Par parcours</h3>
          <div className="mt-2">
            {Object.entries(stats.inscriptionsParParcours)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3)
              .map(([parcours, count], index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-blue-700">{parcours}</span>
                  <span className="font-medium text-blue-900">{count}</span>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-violet-50 border-violet-200 rounded-md border p-4">
          <h3 className="text-violet-900 text-sm font-medium">
            Par année universitaire
          </h3>
          <div className="mt-2">
            {Object.entries(stats.inscriptionsParAnnee)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3)
              .map(([annee, count], index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-violet-700">{annee}</span>
                  <span className="text-violet-900 font-medium">{count}</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <button
          onClick={openCreateReinscriptionModal}
          className="flex flex-1 items-center justify-center gap-2 rounded-md bg-purple-600 px-4 py-2.5 text-white shadow-sm transition-all hover:bg-purple-700"
        >
          <MdHowToReg className="h-5 w-5" />
          <span>Réinscription</span>
        </button>
        <button
          onClick={openCreateInscriptionModal}
          className="flex flex-1 items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2.5 text-white shadow-sm transition-all hover:bg-indigo-700"
        >
          <MdAdd className="h-5 w-5" />
          <span>Nouvelle Inscription</span>
        </button>
      </div>

      <ActionBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Rechercher par nom, niveau, parcours..."
        searchIcon={MdSchool}
        onRefresh={fetchInscriptions}
        showCreateButton={false}
        loading={loading}
        primaryColor="#8B5CF6"
        customActions={
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-gray-700 transition-colors hover:bg-gray-200"
          >
            <MdFilterList className="h-5 w-5" />
            <span>Filtres</span>
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs">
              {Object.values(filters).filter((v) => v !== "").length}
            </span>
          </button>
        }
      />

      {showFilters && (
        <InscriptionFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={resetFilters}
          uniqueAnnees={uniqueAnnees}
          uniqueNiveaux={uniqueNiveaux}
          uniqueParcours={uniqueParcours}
          uniqueMentions={uniqueMentions}
        />
      )}

      {error && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600"
          role="alert"
        >
          {error}
        </div>
      )}

      <InscriptionTable
        inscriptions={filteredInscriptions}
        loading={loading}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
        onView={openDetailModal}
      />
      <InscriptionFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitReinscription}
        initialInscription={currentInscription}
        isEditing={isEditing}
        membres={membres}
        anneesUniv={anneesUniv}
        niveaux={niveaux}
        parcours={parcours}
        mentions={mentions}
        loading={loading}
      />

      <InscriptionWithMembreFormModal
        isOpen={isNewMembreModalOpen}
        onClose={() => setIsNewMembreModalOpen(false)}
        onSubmit={handleSubmitNewInscription}
        initialInscription={currentInscription}
        anneesUniv={anneesUniv}
        niveaux={niveaux}
        parcours={parcours}
        mentions={mentions}
        promotions={promotions}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentInscription}
        itemName={`Inscription de ${
          currentInscription.nom_prenom_membre || ""
        } (${currentInscription.niveau || ""} - ${
          currentInscription.dateuniv || ""
        })`}
        itemType="inscription"
        warning="La suppression de cette inscription est irréversible et peut affecter les données académiques de l'étudiant."
        loading={loading}
      />

      <InscriptionDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        inscription={currentInscription}
        onEdit={() => {
          setIsDetailModalOpen(false);
          setTimeout(() => openEditModal(currentInscription), 300);
        }}
        onDelete={() => {
          setIsDetailModalOpen(false);
          setTimeout(() => openDeleteModal(currentInscription), 300);
        }}
      />
    </div>
  );
};

export default InscriptionManager;
